import _ from "lodash";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { getScheduleDetail_RCB01, getScheduleRoundNOverdueList } from "src/api/billingSchedule/billingSchedule-api";
import { useApiOperation } from "src/api/hooks";
import { ContentsTitle } from "src/components";
import MetaTag from "src/components/layout/MetaTag";
import useNavigate from "src/hooks/usePartnerNavigate";
import useContractApi from "src/pages/contract/hooks/useContractApi";

import { disabledEdit } from "src/api/contract/contract-types";
import ScheduleBasicInfo from "../components/basicInfo/ScheduleBasicInfo";
import ScheduleInfo from "./components/billingScheduleInfo/ScheduleInfo";
import MntBillingDetail from "./components/mntBillingDetail/MntBillingDetail";

const tabs = [
  { value: "basicInfo", label: "기본정보" },
  { value: "schedule", label: "이용료 빌링 현황" },
  { value: "mnt", label: "관리비 빌링 현황" },
];

/**
 * 청구 일정 상세 컴포넌트
 * 청구 일정의 상세 정보를 표시하고 관리합니다.
 *
 * @param id - 라우트 파라미터. 청구 일정 id 가 아니라 계약 id 다 (빌링 스케줄 상세는 계약 단위 화면)
 * @returns 청구 일정 상세 정보 화면
 */
const BillingScheduleDetail = () => {
  const { getContractDetail } = useContractApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [scheduleId, setScheduleId] = useState(0);
  // const [basicInfo, setBasicInfo] = useState<any>();

  const [roundList, setRoundList] = useState<any>([]);
  const [overdueList, setOverdueList] = useState<any>([]);

  const [contractDetail, setContractDetail] = useState<any>({});

  const [spaceProductType, setSpaceProductType] = useState("");

  const [editable, setEditable] = useState(false);

  // 회차별 청구/연체/결제 목록 조회
  const { executeAsync: getRoundList } = useApiOperation(getScheduleRoundNOverdueList);
  // 빌링스케줄 리스트 조회
  const { executeAsync: getScheduleList } = useApiOperation(getScheduleDetail_RCB01);

  // location search (url query parameter) 를 읽어서 object 로 변환
  const queryParams = useMemo(
    () =>
      qs.parse(location.search, {
        ignoreQueryPrefix: true,
        allowDots: true,
        decoder: (value) => value,
      }),
    [location],
  );

  // 현재 활성화 되어야하는 tab
  const activeTab = useMemo(() => {
    if (queryParams?.tab) {
      return tabs.find((tab) => tab.value === queryParams?.tab);
    }
    return tabs[0];
  }, [queryParams]);

  // 탭 클릭시 callback 함수
  const clickTab = (tab: { value: string; label: string }) => {
    // console.log("clickTab", tab);
    const newQueryParams = { ...queryParams };
    if (newQueryParams?.tab) {
      delete newQueryParams.tab;
    }

    newQueryParams.tab = tab.value;

    // query parameter 에 tab 추가
    const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
    navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
  };

  const breadCrumbs = [{ value: "billing", label: "빌링스케줄 상세" }];
  if (activeTab) {
    // 마지막 bread crumb 는 현재 선택된 탭
    breadCrumbs.push({
      value: activeTab.value,
      label: activeTab.label,
    });
  }

  const [stopObj, setStopObj] = useState<any>();

  const callList = async () => {
    if (!id) return;

    //
    const res: any = await getScheduleList({ contractId: Number(id), supplyType: "RENTAL" });

    setStopObj({});
    const response: any = await getRoundList({ scheduleId: Number(res!.data!.data!.scheduleList![0]!.scheduleId!) }).catch(() => undefined);
    if (!response) return;

    const sorted = _.sortBy(response.data.data.content, ["bill.billOrder"]);

    const normal = sorted.filter((sort) => {
      if (sort.bill.isOverdueBill === false) {
        return sort;
      }
    });
    const overdues = sorted.filter((sort) => {
      if (sort.bill.isOverdueBill) {
        return sort;
      }
    });

    setRoundList(normal);
    setOverdueList(overdues);
  };

  const fetchBillDate = async (id: string) => {
    //call contractDetail
    const contractDetailResponse = await getContractDetail(String(id));

    setContractDetail(contractDetailResponse);
    if (disabledEdit.includes(contractDetailResponse?.contract?.contractStep!)) {
      setEditable(true);
    }
    setSpaceProductType(contractDetailResponse.product.productName);

    const sd: any = contractDetailResponse.scheduleList!.find((schedule: any) => schedule.supplyType === "RENTAL");

    // setBasicInfo(contractDetailResponse.contract!);
    setScheduleId(sd.scheduleId);
    await callList();
    setStopObj(sd);

    // }
  };

  useEffect(() => {
    if (id) {
      fetchBillDate(id);
    }
  }, [id]);

  // title 에 건물명 바인딩
  let contractApplyNumber;
  if (contractDetail.contractManage?.contractApplyNumber) {
    contractApplyNumber = contractDetail.contractManage?.contractApplyNumber;
  }
  const checkTabs = useMemo(() => {
    if (
      contractDetail?.contract?.isMntContain! ||
      (contractDetail.product && contractDetail.product.productType === "FULL_COURT" && contractDetail.product.productType === "OPEN_COURT")
    ) {
      return tabs.filter((tab: any) => tab.value !== "mnt");
    }
    return tabs;
  }, [contractDetail]);

  return (
    <>
      <MetaTag subTitle={contractApplyNumber} />
      <div className="page-contract-detail">
        <ContentsTitle
          title="빌링스케줄 상세"
          tabs={checkTabs}
          activeTab={activeTab}
          clickTab={clickTab}
          detailInfo={[
            // 목록의 "빌링 스케줄 ID" 컬럼과 같은 값(이용료 스케줄 id)을 쓴다.
            // 계약 ID / 신청 번호는 기본정보 탭의 신청/계약 정보에서 확인한다.
            { title: "빌링 스케줄 ID", value: scheduleId ? String(scheduleId) : "-" },
          ]}
        />
        {activeTab?.value === "basicInfo" && <ScheduleBasicInfo contractDetail={contractDetail} />}
        {activeTab?.value === "schedule" && (
          <ScheduleInfo
            callList={callList}
            contractBaseInfo={contractDetail}
            roundList={roundList}
            overdueList={overdueList}
            spaceProductType={spaceProductType}
            stopObj={stopObj}
            editable={editable}
            scheduleId={scheduleId}
          />
        )}
        {activeTab?.value === "mnt" && (
          <MntBillingDetail
            basicInfo={contractDetail}
            callList={callList}
            contractBaseInfo={contractDetail}
            spaceProductType={spaceProductType}
            stopObj={stopObj}
            editable={editable}
          />
        )}
      </div>
    </>
  );
};

export default BillingScheduleDetail;
